import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Check, 
  X, 
  Pencil,
  AlertTriangle,
  Shield,
  Calculator,
  Scale,
  MessageSquareWarning,
  ChevronRight,
  Loader2,
  Eye,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

// Flag pattern definitions
export const FLAG_PATTERNS = {
  advice_language: {
    label: 'Advice Language',
    icon: MessageSquareWarning,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    patterns: [
      /\b(should|recommend|advise|suggest|best to|you need to|must|ought to)\b/gi,
      /\b(in my opinion|I think you should|you'd be better off)\b/gi,
    ],
    description: 'Contains advisory language that may constitute professional advice',
  },
  calculations: {
    label: 'Calculations',
    icon: Calculator,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    patterns: [
      /\b(calculate|computation|formula|percentage|rate|amount|total|sum)\b/gi,
      /\d+(\.\d+)?%/g,
      /\$\d+/g,
    ],
    description: 'Contains numerical calculations or financial figures',
  },
  disputes_audits: {
    label: 'Disputes/Audits/Penalties',
    icon: Scale,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    patterns: [
      /\b(audit|dispute|penalty|fine|litigation|lawsuit|legal action|IRD|tax authority)\b/gi,
      /\b(investigation|compliance|breach|violation|enforcement)\b/gi,
    ],
    description: 'References disputes, audits, or regulatory penalties',
  },
  guarantees: {
    label: 'Guarantees',
    icon: Shield,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    patterns: [
      /\b(guarantee|guaranteed|promise|ensure|certain|definitely|always|never)\b/gi,
      /\b(100%|absolutely|without fail|no risk)\b/gi,
    ],
    description: 'Contains guarantee or certainty language',
  },
};

export type FlagType = keyof typeof FLAG_PATTERNS;

export interface ExtractedItem {
  id: string;
  content: string;
  type: 'faq_question' | 'faq_answer' | 'paragraph' | 'heading' | 'list_item' | 'faq_pair';
  source_context?: string;
  status: 'pending' | 'approved' | 'excluded' | 'edited';
  flags: FlagType[];
  original_content?: string;
  edited_at?: string;
  edited_by?: string;
}

// Helper to parse items from JSON with proper typing
const parseExtractedItems = (data: any): ExtractedItem[] => {
  if (!data?.items || !Array.isArray(data.items)) return [];
  return data.items.map((item: any) => ({
    ...item,
    status: item.status || 'pending',
    flags: item.flags || [],
  })) as ExtractedItem[];
};

interface IngestionScrutinyPanelProps {
  asset: {
    id: string;
    asset_type: string;
    file_url: string;
    extracted_json: any;
    flagged_items: any;
  };
  onUpdate: () => void;
}

const IngestionScrutinyPanel: React.FC<IngestionScrutinyPanelProps> = ({ asset, onUpdate }) => {
  const [items, setItems] = useState<ExtractedItem[]>(() => {
    return parseExtractedItems(asset.extracted_json);
  });
  const [editingItem, setEditingItem] = useState<ExtractedItem | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [saving, setSaving] = useState(false);

  const detectFlags = (content: string): FlagType[] => {
    const flags: FlagType[] = [];
    
    Object.entries(FLAG_PATTERNS).forEach(([key, pattern]) => {
      const hasMatch = pattern.patterns.some(regex => regex.test(content));
      if (hasMatch) {
        flags.push(key as FlagType);
      }
    });
    
    return flags;
  };

  const handleApproveItem = async (itemId: string) => {
    const updatedItems = items.map(item => 
      item.id === itemId ? { ...item, status: 'approved' as const } : item
    );
    setItems(updatedItems);
    await saveItems(updatedItems);
    toast.success('Item approved');
  };

  const handleExcludeItem = async (itemId: string) => {
    const updatedItems = items.map(item => 
      item.id === itemId ? { ...item, status: 'excluded' as const } : item
    );
    setItems(updatedItems);
    await saveItems(updatedItems);
    toast.success('Item excluded');
  };

  const handleEditItem = (item: ExtractedItem) => {
    setEditingItem(item);
    setEditedContent(item.content);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    const updatedItems = items.map(item => 
      item.id === editingItem.id 
        ? { 
            ...item, 
            content: editedContent,
            original_content: item.original_content || item.content,
            status: 'edited' as const,
            flags: detectFlags(editedContent),
            edited_at: new Date().toISOString(),
          } 
        : item
    );
    setItems(updatedItems);
    await saveItems(updatedItems);
    setEditingItem(null);
    toast.success('Item edited and approved');
  };

  const handleApproveAll = async () => {
    const updatedItems: ExtractedItem[] = items.map(item => ({
      ...item,
      status: (item.status === 'excluded' ? 'excluded' : 'approved') as ExtractedItem['status'],
    }));
    setItems(updatedItems);
    await saveItems(updatedItems);
    toast.success('All non-excluded items approved');
  };

  const saveItems = async (updatedItems: ExtractedItem[]) => {
    setSaving(true);
    try {
      const approvedItems = updatedItems.filter(i => i.status === 'approved' || i.status === 'edited');
      const flaggedItems = updatedItems.filter(i => i.flags.length > 0 && i.status === 'pending');
      
      // Convert to plain objects for JSON storage
      const itemsForStorage = updatedItems.map(item => ({
        id: item.id,
        content: item.content,
        type: item.type,
        status: item.status,
        flags: item.flags,
        source_context: item.source_context,
        original_content: item.original_content,
        edited_at: item.edited_at,
        edited_by: item.edited_by,
      }));

      const flaggedForStorage = flaggedItems.map(i => ({
        id: i.id,
        flags: i.flags,
        preview: i.content.slice(0, 100),
      }));

      const { error } = await supabase
        .from('tenant_assets')
        .update({
          extracted_json: {
            items: itemsForStorage,
            approved_count: approvedItems.length,
            total_count: updatedItems.length,
            last_reviewed: new Date().toISOString(),
          },
          flagged_items: flaggedItems.length > 0 ? {
            count: flaggedItems.length,
            items: flaggedForStorage,
          } : null,
          ingestion_status: flaggedItems.length === 0 ? 'completed' : 'pending',
        })
        .eq('id', asset.id);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error saving items:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const pendingItems = items.filter(i => i.status === 'pending');
  const approvedItems = items.filter(i => i.status === 'approved' || i.status === 'edited');
  const excludedItems = items.filter(i => i.status === 'excluded');
  const flaggedItems = items.filter(i => i.flags.length > 0 && i.status === 'pending');

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Eye className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No extracted items to review</p>
          <p className="text-sm text-muted-foreground mt-1">
            Items will appear here after document processing
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Total:</span>
          <Badge variant="secondary">{items.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Pending:</span>
          <Badge variant="outline">{pendingItems.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Approved:</span>
          <Badge className="bg-primary/10 text-primary">{approvedItems.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Excluded:</span>
          <Badge variant="secondary">{excludedItems.length}</Badge>
        </div>
        {flaggedItems.length > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span className="text-sm text-destructive font-medium">
              {flaggedItems.length} flagged
            </span>
          </div>
        )}
        <div className="ml-auto">
          <Button 
            size="sm" 
            onClick={handleApproveAll}
            disabled={saving || pendingItems.length === 0}
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Approve All Non-Flagged
          </Button>
        </div>
      </div>

      {/* Flagged Items First */}
      {flaggedItems.length > 0 && (
        <Card className="border-destructive/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Items Requiring Review ({flaggedItems.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {flaggedItems.map(item => (
              <ScrutinyItem
                key={item.id}
                item={item}
                onApprove={() => handleApproveItem(item.id)}
                onExclude={() => handleExcludeItem(item.id)}
                onEdit={() => handleEditItem(item)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* All Items Accordion */}
      <Accordion type="multiple" className="space-y-2">
        {pendingItems.filter(i => i.flags.length === 0).length > 0 && (
          <AccordionItem value="pending" className="border rounded-lg">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <span>Pending Review</span>
                <Badge variant="outline">{pendingItems.filter(i => i.flags.length === 0).length}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              {pendingItems.filter(i => i.flags.length === 0).map(item => (
                <ScrutinyItem
                  key={item.id}
                  item={item}
                  onApprove={() => handleApproveItem(item.id)}
                  onExclude={() => handleExcludeItem(item.id)}
                  onEdit={() => handleEditItem(item)}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        )}

        {approvedItems.length > 0 && (
          <AccordionItem value="approved" className="border rounded-lg border-primary/30">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span>Approved Items</span>
                <Badge className="bg-primary/10 text-primary">{approvedItems.length}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              {approvedItems.map(item => (
                <ScrutinyItem
                  key={item.id}
                  item={item}
                  onApprove={() => {}}
                  onExclude={() => handleExcludeItem(item.id)}
                  onEdit={() => handleEditItem(item)}
                  showActions={false}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        )}

        {excludedItems.length > 0 && (
          <AccordionItem value="excluded" className="border rounded-lg opacity-60">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <X className="w-4 h-4 text-muted-foreground" />
                <span>Excluded Items</span>
                <Badge variant="secondary">{excludedItems.length}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              {excludedItems.map(item => (
                <ScrutinyItem
                  key={item.id}
                  item={item}
                  onApprove={() => handleApproveItem(item.id)}
                  onExclude={() => {}}
                  onEdit={() => handleEditItem(item)}
                  showActions={true}
                  isExcluded={true}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Item Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingItem?.original_content && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Original:</p>
                <p className="text-sm p-3 bg-muted rounded-lg line-through opacity-60">
                  {editingItem.original_content}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium mb-1">Content:</p>
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={5}
                placeholder="Edit the content..."
              />
            </div>
            {editingItem && editingItem.flags.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Current Flags:</p>
                <div className="flex flex-wrap gap-2">
                  {editingItem.flags.map(flag => {
                    const pattern = FLAG_PATTERNS[flag];
                    const Icon = pattern.icon;
                    return (
                      <Badge key={flag} variant="outline" className={`${pattern.bgColor} ${pattern.color}`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {pattern.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <Save className="w-4 h-4 mr-1" />
              Save & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Individual item component
interface ScrutinyItemProps {
  item: ExtractedItem;
  onApprove: () => void;
  onExclude: () => void;
  onEdit: () => void;
  showActions?: boolean;
  isExcluded?: boolean;
}

const ScrutinyItem: React.FC<ScrutinyItemProps> = ({ 
  item, 
  onApprove, 
  onExclude, 
  onEdit,
  showActions = true,
  isExcluded = false,
}) => {
  return (
    <div className={`p-4 border rounded-lg ${
      item.flags.length > 0 ? 'border-destructive/30 bg-destructive/5' : 'border-border'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs capitalize">
              {item.type.replace('_', ' ')}
            </Badge>
            {item.status === 'edited' && (
              <Badge variant="secondary" className="text-xs">
                <Pencil className="w-3 h-3 mr-1" />
                Edited
              </Badge>
            )}
          </div>
          <p className={`text-sm ${isExcluded ? 'line-through text-muted-foreground' : ''}`}>
            {item.content}
          </p>
          {item.flags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.flags.map(flag => {
                const pattern = FLAG_PATTERNS[flag];
                const Icon = pattern.icon;
                return (
                  <Badge 
                    key={flag} 
                    variant="outline" 
                    className={`text-xs ${pattern.bgColor} ${pattern.color}`}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {pattern.label}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
        {showActions && (
          <div className="flex items-center gap-1 shrink-0">
            {!isExcluded && item.status !== 'approved' && item.status !== 'edited' && (
              <Button size="sm" variant="ghost" onClick={onApprove} title="Approve">
                <Check className="w-4 h-4 text-primary" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onEdit} title="Edit">
              <Pencil className="w-4 h-4" />
            </Button>
            {!isExcluded && (
              <Button size="sm" variant="ghost" onClick={onExclude} title="Exclude">
                <X className="w-4 h-4 text-destructive" />
              </Button>
            )}
            {isExcluded && (
              <Button size="sm" variant="ghost" onClick={onApprove} title="Restore">
                <Check className="w-4 h-4 text-primary" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IngestionScrutinyPanel;
